import type { ReploElement } from "schemas/generated/element";

import { useCallback } from "react";

import { successToast } from "@v1/components/designSystem/alert/Toast";
import { elementTypeToEditorData } from "@v1/components/editor/element";
import { setEditorMode } from "@v1/reducers/core-reducer";
import { setLeftBarActiveTab } from "@v1/reducers/ui-reducer";
import { useEditorDispatch } from "@v1/store/store";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { elementRouter, elementUtils } from "@v2/trpc/routes/element";
import { routes } from "@v2/utils/router";
import { generatePath, useLocation, useNavigate } from "react-router-dom";

type UnarchiveOptions = {
  onSuccess?: () => void;
  from: "modal" | "button" | "pane";
};

export function useUnarchiveElement() {
  const dispatch = useEditorDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const projectId = useCurrentProjectId();
  const { mutate: unarchiveMutation, isPending } =
    elementRouter.unarchive.useMutation();

  const handleUnarchive = useCallback(
    (element: ReploElement, options: UnarchiveOptions) => {
      unarchiveMutation(element.id, {
        // TODO (Juan, 2025-01-27): Move onSuccess callback to TRPC mutation in a follow-up PR
        // This will make the code more consistent with other mutations in the app
        onSuccess: () => {
          successToast(
            `${elementTypeToEditorData[element.type].singularDisplayName} unarchived successfully`,
            `Your ${elementTypeToEditorData[element.type].singularDisplayName} has been unarchived successfully`,
          );

          void elementUtils.listArchived.invalidate();
          void elementUtils.listAllElementsMetadata.invalidate();

          navigate(
            `${generatePath(routes.editor.element, {
              projectId,
              elementId: element.id,
            })}${location.search}`,
          );

          dispatch(setEditorMode("edit"));
          dispatch(setLeftBarActiveTab("elements"));

          options.onSuccess?.();
        },
      });
    },
    [unarchiveMutation, navigate, projectId, location.search, dispatch],
  );

  return {
    unarchiveElement: handleUnarchive,
    isLoading: isPending,
  };
}
